import { addMinutes, format } from "date-fns";
import React from "react";
import { msgid, ngettext, t } from "ttag";

import { EventListBlock } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { formatEventType, getTZShortname } from "../../utils/events";
import { Carousel } from "../Carousel";
import { IconButton, LongButton } from "../Clickables";
import { H, HLevel } from "../Headings";
import { RichText } from "../RichText";
import { getThemeClass } from "../ThemedModule";

import styles from "./events-list.module.scss";

export const EventsList = ({ value }: { value: EventListBlock }) => {
    return (
        <section
            className={concatClassNames([
                getThemeClass(value.module_theme),
                styles.root,
            ])}
        >
            {value.eyebrow && <p className={styles.eyebrow}>{value.eyebrow}</p>}
            {value.headline && (
                <H className={styles.headline}>{value.headline}</H>
            )}
            {value.body && <RichText value={value.body} />}
            <Carousel
                containerProps={{ className: styles.carousel }}
                scrollContainerProps={{ className: styles.carouselScroller }}
                label={value.headline ?? t`Unlabeled list of upcoming events`}
                dots={styles.dots}
                controls={({ allVisible }) => (
                    <div
                        className={
                            allVisible ? styles.hiddenControls : styles.controls
                        }
                    >
                        <IconButton icon="caret" slot="previous" />
                        <IconButton icon="caret" slot="next" />
                    </div>
                )}
            >
                {value.events?.map((event) => {
                    const eventStart = event.date ? new Date(event.date) : null;
                    const eventEnd =
                        eventStart && event.duration
                            ? addMinutes(eventStart, event.duration)
                            : null;
                    return (
                        <article className={styles.slide} key={event.id}>
                            <HLevel>
                                <div className={styles.top}>
                                    <div aria-hidden>
                                        {eventStart && (
                                            <>
                                                <div
                                                    className={styles.bigMonth}
                                                >
                                                    {format(eventStart, "MMM")}
                                                </div>
                                                <div className={styles.bigDay}>
                                                    {format(eventStart, "dd")}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    <div className={styles.category}>
                                        {formatEventType(event.event_type)}
                                    </div>
                                </div>
                                <div className={styles.head}>
                                    <H className={styles.eventTitle}>
                                        {event.title}
                                    </H>
                                    <RichText
                                        className={styles.subtitle}
                                        value={event.short_description.value}
                                        blockOverrides={{
                                            unstyled: {
                                                element: "p",
                                            },
                                        }}
                                    />
                                </div>
                                <div className={styles.details}>
                                    {eventStart ? (
                                        <>
                                            <p>
                                                {format(
                                                    eventStart,
                                                    "eee, MMM d, yyyy",
                                                )}{" "}
                                            </p>
                                            <p>
                                                {format(eventStart, "h:mm a")}
                                                {eventEnd &&
                                                    ` - ${format(eventEnd, "h:mm a")}`}{" "}
                                                {getTZShortname(eventStart)}
                                            </p>
                                        </>
                                    ) : (
                                        event.duration && (
                                            <p>
                                                {ngettext(
                                                    msgid`${event.duration} minute`,
                                                    `${event.duration} minutes`,
                                                    event.duration,
                                                )}
                                            </p>
                                        )
                                    )}
                                    {event.attendance_details.value.blocks
                                        .length > 0 && (
                                        <RichText
                                            value={
                                                event.attendance_details.value
                                            }
                                            blockOverrides={{
                                                unstyled: {
                                                    element: "p",
                                                },
                                            }}
                                        />
                                    )}
                                </div>
                                <LongButton
                                    className={styles.cta}
                                    variant="secondary"
                                    href={event.url}
                                >
                                    {t`Learn More`}
                                </LongButton>
                            </HLevel>
                        </article>
                    );
                })}
            </Carousel>
        </section>
    );
};
